import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { useAuth } from '../auth/useAuth'
import { useAsync } from '../lib/useAsync'
import { formatDate, formatMoney } from '../lib/format'
import { canEnterData } from '../lib/types'
import { useSelectedYear } from '../year/useYear'
import type { Deposit, Holder } from '../lib/types'
import { Loading, ErrorState, EmptyState } from '../components/States'
import { DepositSheet } from './DepositSheet'

type HolderKey =
  | 'holder_proprietaire' | 'holder_gerant' | 'holder_mere'
  | 'holder_banque' | 'holder_autre'

interface DepositRow extends Deposit {
  lease: {
    tenant: { full_name: string } | null
    unit: { name: string } | null
  } | null
}

/**
 * Cautions de l'annee consultee.
 *
 * DEUX CAS SONT RETENUS, ET C'EST DELIBERE :
 *   - celles RECUES pendant l'annee
 *   - celles ENCORE DETENUES, quelle que soit l'annee de reception
 *
 * Le second cas sort du cadre annuel, volontairement. Une caution
 * recue en 2025 et toujours detenue aujourd'hui est de l'argent qui
 * existe encore et qu'il faudra rendre : la masquer parce qu'on
 * consulte 2026 ferait disparaitre une dette reelle d'un ecran dont le
 * titre est « total des cautions detenues ». Ces lignes sont donc
 * montrees et signalees comme reportees d'une annee anterieure.
 */
async function load(year: number) {
  const { data, error } = await supabase
    .from('deposits')
    .select(
      'id, lease_id, kind, amount, received_on, held_by, held_by_note,' +
        ' proof_document, status, notes,' +
        ' lease:leases(tenant:tenants(full_name), unit:units(name))',
    )
    .order('status', { ascending: true })
  if (error) throw error

  const toutes = (data ?? []) as unknown as DepositRow[]
  const retenues = toutes
    .map((d) => {
      const anneeRecue = d.received_on ? Number(d.received_on.slice(0, 4)) : null
      const recueCetteAnnee = anneeRecue === year
      const toujoursDetenue = d.status === 'detenu' || d.status === 'introuvable'
      return {
        ...d,
        garder: recueCetteAnnee || toujoursDetenue,
        // Reportee : detenue mais recue avant l'annee consultee.
        reportee: !recueCetteAnnee && toujoursDetenue,
      }
    })
    .filter((d) => d.garder)

  const { data: leases } = await supabase
    .from('leases')
    .select('id, tenant:tenants(full_name), unit:units(name)')
    .in('status', ['actif', 'expire'])

  // Mouvements par caution. Une caution qui en porte ne se supprime
  // pas : ces mouvements sont la trace de l'argent, et la cle
  // etrangere le refuserait de toute facon. On les compte ici pour
  // pouvoir le dire a l'ecran, avec le nombre, au lieu de laisser
  // remonter un message de contrainte.
  const { data: mouvements, error: mErr } = await supabase
    .from('deposit_movements')
    .select('deposit_id')
  if (mErr) throw mErr

  const mouvementsParCaution = new Map<string, number>()
  for (const m of (mouvements ?? []) as { deposit_id: string }[]) {
    mouvementsParCaution.set(
      m.deposit_id,
      (mouvementsParCaution.get(m.deposit_id) ?? 0) + 1,
    )
  }

  return {
    deposits: retenues,
    mouvementsParCaution,
    leases: (leases ?? []) as unknown as {
      id: string
      tenant: { full_name: string } | null
      unit: { name: string } | null
    }[],
  }
}

export function DepositsScreen({ onBack }: { onBack: () => void }) {
  const { t } = useI18n()
  const { profile } = useAuth()
  const { year } = useSelectedYear()
  const { data, loading, error, reload } = useAsync(() => load(year), [year])
  const [sheet, setSheet] = useState<'closed' | 'new'>('closed')
  const [editing, setEditing] = useState<Deposit | null>(null)

  const peutSaisir = canEnterData(profile?.role ?? null)
  const deposits = data?.deposits ?? []

  // Total detenu et repartition par detenteur. Les cautions
  // introuvables sont comptees a part et montrees en rouge.
  const detenues = deposits.filter((d) => d.status === 'detenu')
  const introuvables = deposits.filter((d) => d.status === 'introuvable')
  const total = detenues.reduce((s, d) => s + d.amount, 0)

  const parDetenteur = new Map<Holder, number>()
  for (const d of detenues) {
    parDetenteur.set(d.held_by, (parDetenteur.get(d.held_by) ?? 0) + d.amount)
  }

  return (
    <div className="screen">
      <button type="button" className="backlink" onClick={onBack}>
        ← {t('back')}
      </button>

      <div className="screen__head">
        <h1 className="screen__title">{t('deposits_title')}</h1>
        {peutSaisir && (
          <button
            type="button"
            className="btn btn--primary btn--small"
            onClick={() => setSheet('new')}
          >
            {t('add')}
          </button>
        )}
      </div>

      {loading && <Loading />}
      {error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && deposits.length === 0 && (
        <EmptyState title={t('deposits_none')} />
      )}

      {deposits.length > 0 && (
        <>
          <div className="card">
            <span className="card__caption">{t('deposits_total_held')}</span>
            <span className="card__figure">{formatMoney(total)}</span>

            <h3 className="screen__section">{t('deposits_by_holder')}</h3>
            <dl className="deflist">
              {[...parDetenteur.entries()].map(([h, montant]) => (
                <>
                  <dt key={`${h}-dt`}>{t(`holder_${h}` as HolderKey)}</dt>
                  <dd key={`${h}-dd`}>{formatMoney(montant)}</dd>
                </>
              ))}
            </dl>

            {/* Une caution introuvable est une anomalie : en rouge, et
                jamais fondue dans le total detenu. */}
            {introuvables.length > 0 && (
              <div className="banner banner--error">
                <strong>{t('deposit_status_introuvable')}</strong>
                <span>
                  {formatMoney(introuvables.reduce((s, d) => s + d.amount, 0))}
                </span>
                <span>
                  {introuvables
                    .map((d) => d.lease?.tenant?.full_name ?? '')
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </div>
            )}
          </div>

          <ul className="list">
            {deposits.map((d) => (
              <li key={d.id} className="list__item">
                <span className="list__main">
                  {/* Noms issus de la base, jamais traduits. */}
                  <span className="list__name">
                    {d.lease?.tenant?.full_name ?? ''}
                  </span>
                  <span className="list__meta">
                    {d.lease?.unit?.name ?? ''} ·{' '}
                    {t(`deposit_kind_${d.kind}` as
                      'deposit_kind_caution' | 'deposit_kind_mois_avance'
                      | 'deposit_kind_frais_agence')}
                  </span>
                  <span className="list__note">
                    {t('deposit_held_by')} : {t(`holder_${d.held_by}` as HolderKey)}
                    {d.received_on ? ` · ${formatDate(d.received_on)}` : ''}
                  </span>
                  {!d.proof_document && (
                    <span className="chip chip--warn">{t('deposit_no_proof')}</span>
                  )}
                  {/* Detenue mais recue avant l'annee consultee. */}
                  {d.reportee && (
                    <span className="list__note">{t('deposit_carried_over')}</span>
                  )}
                  {peutSaisir && (
                    <span className="list__row">
                      <button
                        type="button"
                        className="btn btn--ghost btn--small"
                        onClick={() => setEditing(d)}
                      >
                        {t('edit')}
                      </button>
                    </span>
                  )}
                </span>
                <span className="list__side">
                  <span className="list__amount">{formatMoney(d.amount)}</span>
                  <span
                    className={
                      'chip ' +
                      (d.status === 'introuvable'
                        ? 'chip--error'
                        : d.status === 'detenu'
                          ? 'chip--ok'
                          : 'chip--warn')
                    }
                  >
                    {t(`deposit_status_${d.status}` as
                      'deposit_status_detenu' | 'deposit_status_rembourse'
                      | 'deposit_status_applique' | 'deposit_status_introuvable')}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {sheet === 'new' && (
        <DepositSheet
          leases={data?.leases ?? []}
          onClose={() => setSheet('closed')}
          onSaved={() => {
            setSheet('closed')
            reload()
          }}
        />
      )}

      {editing && (
        <DepositSheet
          leases={data?.leases ?? []}
          deposit={editing}
          movementCount={data?.mouvementsParCaution.get(editing.id) ?? 0}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            reload()
          }}
          onDeleted={() => {
            setEditing(null)
            reload()
          }}
        />
      )}
    </div>
  )
}
